import React, { memo, type JSX } from 'react';
import { without, clone } from 'ramda';

import { TEDMixedType } from '../../../../../../../Types/GraphQLTypes';
import query from './EkonomiskAnalysDetailQuery.gql';
import {
  ekanalysresultatdetaljlopandeVariables as IVariables,
  ekanalysresultatdetaljlopande as IData,
} from './_QueryTypes/ekanalysresultatdetaljlopande';
import Logger from '../../../../../../../Utils/Logger';
import { Query } from '@apollo/client/react/components';
import ServerError from '../../../../../../../Components/ServerError/ServerError';
import EkonomiskAnalysStandardColumnQuery from '../../Components/SideMenu/Components/PeriodOchKolumner/EditColumn/Components/Queries/EkonomiskAnalysStandardColumnQuery/EkonomiskAnalysStandardColumnQuery';
import { isBudgetColumn } from '../../Components/SideMenu/Components/PeriodOchKolumner/EditColumn/Utils/EkonomiskAnalysEditColumnUtils';
import { ekanalys_ekanalys_columns } from '../EkonomiskAnalysSettingsQuery/_QueryTypes/ekanalys';
import EDAvenyProgress from '../../../../../../../BaseComponents/ProgressIndicators/AvenyProgress/EDAvenyProgress';

export interface ITEDAvenyEKAnalysColumnData {
  fieldname: string | null;
  rubrik1: string | null;
  rubrik2: string | null;
  rubrik3: string | null;
  visible: boolean | null;
  fieldtype: string | null;
  data: TEDMixedType | null;
}

interface IChildrenProps {
  isBudgetColumn: boolean;
  analysDetail: ITEDAvenyEKAnalysColumnData[];
}

interface IProps {
  column: ekanalys_ekanalys_columns;
  variables: IVariables;
  lopsaldoDetaljrad: boolean;
  children: (childProps: IChildrenProps) => JSX.Element;
}

// Eftersom vi alltid vill ha Definitivbokförd på 1:a plats och fakturabild på plats 2!
const moveColumns = (
  columnDataInput: ITEDAvenyEKAnalysColumnData[],
  lopsaldoDetaljrad: boolean
) => {
  let columnData = clone(columnDataInput);
  const vernrCol = columnData.find(e => e.fieldtype === 'vernr');
  const journalnrCol = columnData.find(e => e.fieldtype === 'journalnr');
  const lopnrCol = columnData.find(e => e.fieldtype === 'lopnr');
  const definitivCol = columnData.find(e => e.fieldtype === 'definitivbokford');
  const fakturabildCol = columnData.find(e => e.fieldtype === 'fakturabild');
  const bokforderUnderlagCol = columnData.find(
    e => e.fieldtype === 'bokfunderlag'
  );
  const vertextColIndex = columnData.findIndex(e => e.fieldtype === 'vertext');
  const saldoColIndex = columnData.findIndex(e => e.fieldtype === 'saldo');

  if (
    vernrCol == null ||
    journalnrCol == null ||
    definitivCol == null ||
    fakturabildCol == null ||
    lopnrCol == null ||
    bokforderUnderlagCol == null
  ) {
    Logger.error(
      Error(
        `Could not move column order. vernrCol: ${vernrCol}, journalnrCol: ${journalnrCol}, definitivCol: ${definitivCol}, fakturabildCol: ${fakturabildCol}, lopnrCol: ${lopnrCol}, bokforderUnderlagCol: ${bokforderUnderlagCol}`
      )
    );
    return columnData;
  }

  if (
    lopsaldoDetaljrad &&
    vertextColIndex > saldoColIndex &&
    vertextColIndex !== -1 &&
    saldoColIndex !== -1
  ) {
    const vertextCol = columnData[vertextColIndex];
    columnData.splice(vertextColIndex, 1);
    columnData.splice(saldoColIndex, 0, vertextCol);
  }

  return [
    journalnrCol,
    vernrCol,
    lopnrCol,
    definitivCol,
    fakturabildCol,
    bokforderUnderlagCol,
    ...without(
      [
        journalnrCol,
        vernrCol,
        lopnrCol,
        definitivCol,
        fakturabildCol,
        bokforderUnderlagCol,
      ],
      columnData
    ),
  ].filter(col => {
    return (
      !lopsaldoDetaljrad ||
      !(col.fieldtype === 'kredit' || col.fieldtype === 'debet')
    );
  });
};

const EkonomiskAnalysDetailQuery: React.FunctionComponent<IProps> = (
  props: IProps
) => {
  return (
    <EkonomiskAnalysStandardColumnQuery>
      {({
        standardColumns,
        loading: standardColumnsLoading,
        error: standardColumnsError,
      }) => {
        if (standardColumnsError) {
          return <ServerError error={standardColumnsError} />;
        } else if (!standardColumns) {
          Logger.error('Done loading and no errors but no data');
          return <span>Error</span>;
        } else {
          return (
            <Query<IData, IVariables>
              query={query}
              variables={props.variables}
              fetchPolicy="cache-and-network"
            >
              {({ data, error, refetch, loading }) => {
                if (
                  (!error && (!data || !Object.keys(data).length)) ||
                  loading
                ) {
                  return (
                    <div style={{ padding: '20px' }}>
                      <EDAvenyProgress />
                    </div>
                  );
                } else if (error) {
                  return <ServerError error={error} />;
                } else {
                  if (!data || data.ekanalysresultatdetaljlopande == null) {
                    Logger.error('Done loading and no errors but no data');
                    return null;
                  } else if (
                    data.ekanalysresultatdetaljlopande.columns == null ||
                    data.ekanalysresultatdetaljlopande.columns.length === 0
                  ) {
                    return null;
                  } else {
                    if (isBudgetColumn(standardColumns, props.column)) {
                      return props.children({
                        isBudgetColumn: true,
                        analysDetail:
                          data.ekanalysresultatdetaljlopande.columns,
                      });
                    } else {
                      return props.children({
                        isBudgetColumn: false,
                        analysDetail: moveColumns(
                          data.ekanalysresultatdetaljlopande.columns,
                          props.lopsaldoDetaljrad
                        ),
                      });
                    }
                  }
                }
              }}
            </Query>
          );
        }
      }}
    </EkonomiskAnalysStandardColumnQuery>
  );
};

export default memo(EkonomiskAnalysDetailQuery);
